import { useState, useRef, useMemo, useCallback } from "react";
import { useDispatch, useSelector } from "react-redux";
import { RootState } from "../../../../../../../../../../redux/store";
import ReplyIcon from "../../../../../../../../../../assets/svg/reply-icon";
import { ActivityContentShimmer } from "./components/ActivityContentShimmer";
import {
  TicketCommentInput,
  type TicketChannelType,
} from "./components/TicketCommentInput";
import {
  openComposeEmailModal,
  setSelectedTab,
} from "../../../../../../../../../../redux/slices/composeEmailModalSlice";
import {
  setRecipients,
  setSubject as setEditorSubject,
  resetEmailEditor,
} from "../../../../../../../../../../redux/slices/emailEditorSlice";
import DeleteCommentModal from "./components/DeleteCommentModal";
import ActivityThreadView from "./components/ActivityThreadView";
import ActivityCommentItem from "./components/ActivityCommentItem";
import ActivityMainHeader from "./components/ActivityMainHeader";
import {
  useGetTicketCommentsQuery,
  useDeleteTicketCommentMutation,
} from "../../../../../../../../../../redux/services/helpdeskApi";
import { useOrgUsersList } from "../../../../../../../../../../hooks/useOrgUsersList";
import { useGetIntegrationEmailsQuery } from "../../../../../../../../../../redux/services/communicationApi";
import {
  type TicketComment,
  type TicketMail,
  type TicketCommentCreatedBy,
  type ActivityItem,
} from "./components/CommentCard";
import ActivityEmailItem from "./components/ActivityEmailItem";
import ActivityExpandedModal from "./components/ActivityExpandedModal";
import AttachmentPreviewModal from "../../../../../../../../../Communications/components/CommunicationDetailsPanel/components/AttachmentPreviewModal";
import { AttachmentPreviewItem } from "../../../../../../../../../Communications/utils/types";
import { useDebounce } from "../../../../../../../../../../hooks/useDebounce";
import { PermissionDisabledWrapper } from "../../../../../PermissionDisabledWrapper";
import { useHasHelpdeskUpdateAccess } from "../../../../../../utils/helpdeskPermissions";

interface ActivityContentProps {
  ticketId?: number;
  assignees?: string[];
  ticketCategory?: string;
  ticketCreatedById?: string;
}

export const ActivityContent = ({
  ticketId,
  assignees,
  ticketCategory,
  ticketCreatedById,
}: ActivityContentProps) => {
  const dispatch = useDispatch();
  const currentUserId = useSelector(
    (state: RootState) => state?.auth?.user?._id
  );
  const canUpdate = useHasHelpdeskUpdateAccess(ticketCategory, ticketCreatedById);
  const [selectedChannel, setSelectedChannel] =
    useState<TicketChannelType>("comment");
  const [sortOrder, setSortOrder] = useState<"newest" | "oldest" | null>(null);
  const [previewOpen, setPreviewOpen] = useState(false);
  const [previewIndex, setPreviewIndex] = useState(0);
  const [previewImages, setPreviewImages] = useState<AttachmentPreviewItem[]>(
    []
  );
  const [isSearchOpen, setIsSearchOpen] = useState(false);
  const [searchValue, setSearchValue] = useState("");
  const [isExpandedModal, setIsExpandedModal] = useState(false);

  const debouncedSearch = useDebounce(searchValue, 300);

  const apiSortOrder =
    sortOrder === "newest"
      ? "desc"
      : sortOrder === "oldest"
        ? "asc"
        : undefined;

  const { data: commentsData, isFetching: isCommentsFetching } =
    useGetTicketCommentsQuery(
      {
        ticket_id: String(ticketId),
        sort_order: apiSortOrder,
        search: debouncedSearch?.trim() || undefined,
      },
      { skip: !ticketId }
    );

  const [deleteTicketComment] = useDeleteTicketCommentMutation();
  const [enableSort, setEnableSort] = useState<string | null>(null);
  const [expandedActivities, setExpandedActivities] = useState<Set<string>>(
    new Set()
  );
  const [overflowingActivities, setOverflowingActivities] = useState<
    Set<string>
  >(new Set());
  const [openMenuId, setOpenMenuId] = useState<string | null>(null);
  const [editingCommentId, setEditingCommentId] = useState<string | null>(null);
  const [pendingDeleteCommentId, setPendingDeleteCommentId] = useState<
    string | null
  >(null);
  const [isDeletingComment, setIsDeletingComment] = useState(false);
  const [replyingToComment, setReplyingToComment] =
    useState<TicketComment | null>(null);
  const [viewingMailThread, setViewingMailThread] =
    useState<TicketMail | null>(null);
  const contentRefs = useRef<Map<string, HTMLDivElement>>(new Map());
  const sortButtonRef = useRef<HTMLDivElement>(null);
  const searchInputRef = useRef<HTMLInputElement>(null);
  const observersRef = useRef<Map<string, ResizeObserver>>(new Map());

  const refCallbacksCache = useRef<
    Map<string, (el: HTMLDivElement | null) => void>
  >(new Map());

  const getContentRef = useCallback((id: string) => {
    let cb = refCallbacksCache.current?.get(id);
    if (!cb) {
      cb = (el: HTMLDivElement | null) => {
        const oldObserver = observersRef.current.get(id);
        if (oldObserver) {
          oldObserver.disconnect();
          observersRef.current.delete(id);
        }
        if (el) {
          contentRefs.current.set(id, el);
          const observer = new ResizeObserver(() => {
            const isOver = el.scrollHeight > el.clientHeight;
            if (isOver) {
              setOverflowingActivities((prev) => {
                if (prev.has(id)) return prev;
                const next = new Set(prev);
                next.add(id);
                return next;
              });
            }
          });
          observer.observe(el);
          observersRef.current.set(id, observer);
        } else {
          contentRefs.current?.delete(id);
        }
      };
      refCallbacksCache.current.set(id, cb);
    }
    return cb;
  }, []);

  const toggleActivityExpansion = useCallback((id: string) => {
    setExpandedActivities((prev) => {
      const next = new Set(prev);
      if (next?.has(id)) next?.delete(id);
      else next?.add(id);
      return next;
    });
  }, []);

  const activityItems: ActivityItem[] = useMemo(() => {
    const merged: ActivityItem[] = [
      ...((commentsData?.comments ?? commentsData ?? []) as TicketComment[]).map(
        (comment) => ({ ...comment, _type: "comment" as const })
      ),
      ...((commentsData?.mails ?? []) as TicketMail[]).map(
        (mail) => ({ ...mail, _type: "mail" as const })
      ),
    ];
    merged.sort((a, b) => {
      const diff =
        new Date(a.created_at).getTime() - new Date(b.created_at).getTime();
      return sortOrder === "newest" ? -diff : diff;
    });
    return merged;
  }, [commentsData, sortOrder]);

  const handleSortAction = (order: "newest" | "oldest") => {
    if (sortOrder === order) {
      setSortOrder(null);
    } else {
      setSortOrder(order);
    }
    setEnableSort(null);
  };

  const handleResetSort = () => {
    setSortOrder(null);
    setEnableSort(null);
  };

  const handleSortToggle = () => {
    setEnableSort(enableSort === "activity" ? null : "activity");
  };

  const handleReplyClick = (comment: TicketComment) => {
    setReplyingToComment(comment);
  };

  const handleConfirmDelete = useCallback(async () => {
    if (!pendingDeleteCommentId) return;
    setIsDeletingComment(true);
    try {
      await deleteTicketComment(pendingDeleteCommentId);
      setReplyingToComment((current) =>
        current !== null && current.id === pendingDeleteCommentId
          ? null
          : current
      );
      setPendingDeleteCommentId(null);
    } finally {
      setIsDeletingComment(false);
    }
  }, [pendingDeleteCommentId, deleteTicketComment]);

  const handleBackFromThread = () => {
    setReplyingToComment(null);
  };

  const handleSearchOpen = () => {
    setIsSearchOpen(true);
    setTimeout(() => searchInputRef.current?.focus(), 310);
  };

  const handleSearchClose = () => {
    setIsSearchOpen(false);
    setSearchValue("");
  };

  const handleOpenExpandedModal = useCallback(() => {
    setIsExpandedModal(true);
  }, []);

  const handleCloseExpandedModal = useCallback(() => {
    setIsExpandedModal(false);
  }, []);

  const { activeUsers } = useOrgUsersList();
  const { data: integrationEmails, isError: isIntegrationEmailsError } =
    useGetIntegrationEmailsQuery({});

  const assigneeEmails = useMemo(() => {
    if (!assignees?.length || !activeUsers?.length) return [];
    const usersMap = new Map<string, string>();
    for (const user of activeUsers) {
      if (user.email) usersMap.set(user._id, user.email);
    }
    return assignees
      .map((id) => usersMap.get(id))
      .filter((email): email is string => Boolean(email));
  }, [assignees, activeUsers]);

  const helpdeskSourceMetadata = useMemo(
    () => ({
      ticketId: String(ticketId),
      integrationEmails: integrationEmails ?? [],
    }),
    [ticketId, integrationEmails]
  );

  const handleComposeEmail = () => {
    dispatch(resetEmailEditor());
    dispatch(
      openComposeEmailModal({
        viewState: "mini",
        source: "helpdesk",
        sourceMetadata: helpdeskSourceMetadata,
      })
    );
    dispatch(setRecipients(assigneeEmails));
  };

  const handleTemplates = () => {
    dispatch(resetEmailEditor());
    dispatch(
      openComposeEmailModal({
        viewState: "expanded",
        source: "helpdesk",
        sourceMetadata: helpdeskSourceMetadata,
      })
    );
    dispatch(setRecipients(assigneeEmails));
    dispatch(setSelectedTab("my_templates"));
  };

  const handleReplyToMail = (mail: TicketMail) => {
    dispatch(resetEmailEditor());
    const replyRecipients = mail.mail_data?.to?.length
      ? mail.mail_data.to
      : [mail.mail_data?.from_email].filter(Boolean);
    const replySubject = mail.mail_data?.subject
      ? mail.mail_data.subject.startsWith("Re:")
        ? mail.mail_data.subject
        : `Re: ${mail.mail_data.subject}`
      : "";
    dispatch(
      openComposeEmailModal({
        viewState: "mini",
        source: "helpdesk",
        sourceMetadata: {
          ...helpdeskSourceMetadata,
          extraAPIPayload: {
            thread_id: mail.thread_id,
            parent_mail_id: mail.id,
          },
        },
        subject: replySubject,
      })
    );
    dispatch(setRecipients(replyRecipients));
    dispatch(setEditorSubject(replySubject));
  };

  const resolveCreatedByName = (
    createdBy: TicketCommentCreatedBy | undefined
  ): string | undefined => {
    if (typeof createdBy === "object") return createdBy.name;
    return undefined;
  };

  const resolveCreatedById = (
    createdBy: TicketCommentCreatedBy | undefined
  ): string | undefined =>
    typeof createdBy === "object" ? createdBy?.id : createdBy;

  const buildCommentImages = useCallback(
    (comment: TicketComment): AttachmentPreviewItem[] => {
      const attachmentImages: AttachmentPreviewItem[] = (
        comment?.attachments ?? []
      )
        .filter((att) => att?.content_type?.startsWith("image/"))
        .map((att) => ({
          s3_key: att.s3_key,
          s3_url: att.s3_url,
          file_name: att.file_name,
          content_type: att.content_type,
          timestamp: comment.created_at,
        }));

      const seen = new Set(attachmentImages?.map((a) => a.s3_url));
      const inlineImages: AttachmentPreviewItem[] = [];
      if (comment?.content) {
        const div = document.createElement("div");
        div.innerHTML = comment.content;
        div?.querySelectorAll("img").forEach((img) => {
          const src = img?.getAttribute("src") || "";
          if (src && !seen?.has(src)) {
            inlineImages?.push({
              s3_url: src,
              file_name: img?.getAttribute("alt") || "image",
              content_type: "image/",
              timestamp: comment.created_at,
            });
            seen?.add(src);
          }
        });
      }
      return [...attachmentImages, ...inlineImages];
    },
    []
  );

  const openPreview = useCallback(
    (comment: TicketComment, index: number) => {
      const images = buildCommentImages(comment);
      setPreviewImages(images);
      setPreviewIndex(index);
      setPreviewOpen(true);
    },
    [buildCommentImages]
  );

  const getCommentItemProps = (comment: TicketComment) => ({
    comment,
    isExpanded: expandedActivities.has(comment.id),
    isOverflowing: overflowingActivities.has(comment.id),
    isEditing: editingCommentId === comment.id,
    isMenuOpen: openMenuId === comment.id,
    commentImages: buildCommentImages(comment),
    canUpdate,
    isOwnComment: resolveCreatedById(comment?.created_by) === currentUserId,
    contentRef: getContentRef(comment.id),
    onToggleExpansion: () => toggleActivityExpansion(comment.id),
    onReply: () => handleReplyClick(comment),
    onEditCancel: () => setEditingCommentId(null),
    onEditStart: () => {
      setEditingCommentId(comment.id);
      setOpenMenuId(null);
    },
    onDeleteRequest: () => {
      setPendingDeleteCommentId(comment.id);
      setOpenMenuId(null);
    },
    onMenuToggle: () =>
      setOpenMenuId(openMenuId === comment.id ? null : comment.id),
    onMenuClose: () => setOpenMenuId(null),
    onAttachmentPreview: (index: number) => openPreview(comment, index),
  });

  if (replyingToComment && !isCommentsFetching) {
    const parentComment =
      (activityItems.find(
        (c) => c._type === "comment" && c.id === replyingToComment?.id
      ) as (TicketComment & { _type: "comment" }) | undefined) ??
      replyingToComment;

    const replyCount = parentComment?.replies?.length ?? 0;

    return (
      <div className="flex flex-col h-full bg-pure-white dark:bg-pure-white-dk">
        <ActivityThreadView
          threadAuthorName={resolveCreatedByName(parentComment?.created_by)}
          ticketId={ticketId ? String(ticketId) : undefined}
          parentCommentId={parentComment?.id}
          canUpdate={canUpdate}
          onBack={handleBackFromThread}
        >
          <ActivityCommentItem
            {...getCommentItemProps(parentComment)}
            showReplyButton={false}
            showRepliesBar={false}
          />

          {replyCount > 0 && (
            <div className="flex items-center justify-center gap-1 py-2 bg-hover-effect dark:bg-hover-effect-dk border-b border-border-gray dark:border-border-gray-dk">
              <ReplyIcon className="w-[18px] h-[18px] text-verba-indigo dark:text-verba-indigo-dk" />
              <p className="text-xs font-medium text-verba-indigo dark:text-verba-indigo-dk">
                {replyCount} {replyCount === 1 ? "Reply" : "Replies"}
              </p>
            </div>
          )}

          {parentComment?.replies?.map((reply) => (
            <ActivityCommentItem
              key={reply.id}
              {...getCommentItemProps(reply)}
              showReplyButton={false}
              showRepliesBar={false}
            />
          ))}
        </ActivityThreadView>

        <DeleteCommentModal
          open={!!pendingDeleteCommentId}
          onOpenChange={(open) => {
            if (!open) setPendingDeleteCommentId(null);
          }}
          isDeleting={isDeletingComment}
          onConfirmDelete={handleConfirmDelete}
        />

        {previewImages?.length > 0 && (
          <AttachmentPreviewModal
            open={previewOpen}
            onOpenChange={setPreviewOpen}
            attachments={previewImages}
            initialIndex={previewIndex}
          />
        )}
      </div>
    );
  }

  if (viewingMailThread && !isCommentsFetching) {
    const parentMail =
      (activityItems.find(
        (c) => c._type === "mail" && c.id === viewingMailThread?.id
      ) as (TicketMail & { _type: "mail" }) | undefined) ?? viewingMailThread;

    const threadReplyCount = parentMail?.replies?.length ?? 0;

    return (
      <div className="flex flex-col h-full bg-pure-white dark:bg-pure-white-dk">
        <ActivityThreadView
          threadAuthorName={resolveCreatedByName(parentMail?.created_by)}
          threadSubject={parentMail.mail_data?.subject}
          threadEmailCount={threadReplyCount}
          showCommentInput={false}
          onBack={() => setViewingMailThread(null)}
        >
          <ActivityEmailItem
            mail={parentMail}
            onReply={() => handleReplyToMail(parentMail)}
          />

          {threadReplyCount > 0 && (
            <div className="flex items-center justify-center gap-1 py-2 bg-hover-effect dark:bg-hover-effect-dk border-b border-border-gray dark:border-border-gray-dk">
              <ReplyIcon className="w-[18px] h-[18px] text-verba-indigo dark:text-verba-indigo-dk" />
              <p className="text-xs font-medium text-verba-indigo dark:text-verba-indigo-dk">
                {threadReplyCount}{" "}
                {threadReplyCount === 1 ? "Email Reply" : "Email Replies"}
              </p>
            </div>
          )}

          {parentMail?.replies?.map((reply) => (
            <ActivityEmailItem
              key={reply.id}
              mail={reply}
              variant="compact"
            />
          ))}
        </ActivityThreadView>
      </div>
    );
  }

  return (
    <div className="flex flex-col h-full bg-pure-white dark:bg-pure-white-dk">
      <ActivityMainHeader
        showControls={activityItems.length > 1 || isSearchOpen}
        isSearchOpen={isSearchOpen}
        searchValue={searchValue}
        enableSort={enableSort}
        sortOrder={sortOrder}
        sortButtonRef={sortButtonRef}
        searchInputRef={searchInputRef}
        onSearchOpen={handleSearchOpen}
        onSearchClose={handleSearchClose}
        onSearchChange={setSearchValue}
        onSortToggle={handleSortToggle}
        onSortClose={() => setEnableSort(null)}
        onSort={handleSortAction}
        onSortReset={handleResetSort}
        onExpand={handleOpenExpandedModal}
      />

      <div className="flex-1 overflow-y-auto" data-activity-scroll>
        {isCommentsFetching ? (
          <ActivityContentShimmer />
        ) : (
          activityItems.map((item) =>
            item._type === "mail" ? (
              <ActivityEmailItem
                key={`mail-${item.id}`}
                mail={item}
                onReply={() => handleReplyToMail(item)}
                onShowThread={() => setViewingMailThread(item)}
              />
            ) : (
              <ActivityCommentItem
                key={`comment-${item.id}`}
                {...getCommentItemProps(item)}
              />
            )
          )
        )}
      </div>
      <PermissionDisabledWrapper disabled={!canUpdate}>
        <TicketCommentInput
          ticketId={ticketId ? String(ticketId) : undefined}
          selectedChannel={selectedChannel}
          onChannelChange={setSelectedChannel}
          onComposeEmail={handleComposeEmail}
          onTemplates={handleTemplates}
          isVerbaMailDisabled={isIntegrationEmailsError}
        />
      </PermissionDisabledWrapper>

      <DeleteCommentModal
        open={!!pendingDeleteCommentId}
        onOpenChange={(open) => {
          if (!open) setPendingDeleteCommentId(null);
        }}
        isDeleting={isDeletingComment}
        onConfirmDelete={handleConfirmDelete}
      />

      {previewImages?.length > 0 && (
        <AttachmentPreviewModal
          open={previewOpen}
          onOpenChange={setPreviewOpen}
          attachments={previewImages}
          initialIndex={previewIndex}
        />
      )}

      {isExpandedModal && (
        <ActivityExpandedModal
          ticketId={ticketId}
          sortOrder={sortOrder}
          canUpdate={canUpdate}
          onClose={handleCloseExpandedModal}
        />
      )}
    </div>
  );
};
